import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import {
  Button,
  PopupWindow,
  Table,
  MessagePopupCore,
} from '@utahdts/dts-react-common';
import Icon from '../misc/Icon';
import appStore from '../app/mobx/appStore';
import columns from './columns/PatentColumns';
import webservice from '../app/webservice/webservice';
import Patent from './modals/Patent';
import security from '../app/roles/security';
import roles from '../app/roles/roles';
import { sortByLastUpdatedDesc } from '../misc/Shared';

const Patents = () => {
  const [isBusy, setBusy] = useState(true);
  const [patents, setPatents] = useState([]);
  const [showPatent, displayPatent] = useState(false);
  const [showDelete, displayDelete] = useState(false);
  const [patent, setPatent] = useState({});
  const [isNew, setIsNew] = useState(true);
  // Companies (with FEINs) for the Administrator "Company" selector on a new
  // history; empty for Org Administrators (they file against their current org).
  const [orgOptions, setOrgOptions] = useState([]);

  const displayError = useCallback((error) => {
    // Enhance logging to ensure something is always printed to the console.
    if (error) {
      console.error('An error occurred:', error);
    } else {
      console.warn(
        'displayError was called, but no error object was provided. This may indicate a promise was rejected without a value.',
      );
    }
    setBusy(false);
    MessagePopupCore.addMessage({
      title: 'Something went wrong',
      message: 'An error occurred. Please try again later.',
    });
  }, []);

  const getPatents = useCallback(() => {
    const isPrivilegedUser = security.hasAnyRole([roles.dev, roles.admin]);
    const hasOrg = !!appStore.currentOrg;

    if (isPrivilegedUser || hasOrg) {
      setBusy(true);
      const orgId = isPrivilegedUser ? null : appStore.currentOrg;
      webservice.wac
        .patentLostPerOrg(orgId)
        .then((data) => {
          const patentData = data.patentLostByOrg || [];
          setPatents(patentData);
          setBusy(false);
        })
        .catch(displayError);
    } else {
      setPatents([]);
      setBusy(false);
    }
  }, [appStore.currentOrg, displayError]);

  useEffect(() => {
    getPatents();
  }, [getPatents]);

  // Administrators have no single selected org, so a new history must be
  // associated with a company they pick. Load the companies for the selector;
  // Org Administrators use their current org and get no list.
  const getOrgs = useCallback(() => {
    if (!security.hasAnyRole([roles.dev, roles.admin])) {
      setOrgOptions([]);
      return;
    }
    const result =
      webservice.company &&
      typeof webservice.company.all === 'function' &&
      webservice.company.all();
    if (result && typeof result.then === 'function') {
      result
        .then((data) => setOrgOptions(data?.orgs || []))
        .catch(() => setOrgOptions([]));
    }
  }, []);

  useEffect(() => {
    getOrgs();
  }, [getOrgs]);

  const editPatent = useCallback(
    (isNew, patentToEdit, shouldShow) => {
      setIsNew(isNew);
      const orgID = security.hasAnyRole([roles.dev, roles.admin])
        ? ''
        : appStore.currentOrg;
      const newPatent = {
        ...patentToEdit,
        org: patentToEdit.org ?? orgID,
      };
      setPatent(newPatent);
      displayPatent(shouldShow);
    },
    [appStore.currentOrg],
  );

  const resetPatent = useCallback(() => {
    editPatent(true, {}, false);
    displayDelete(false);
  }, [editPatent]);

  const deletePatent = useCallback((patentToDelete) => {
    console.log(
      '[Delete Patent] Initiating delete for patent:',
      patentToDelete,
    );
    console.log('[Delete Patent] Patent ID:', patentToDelete?.id);
    setPatent(patentToDelete);
    displayDelete(true);
  }, []);

  const actualDelete = useCallback(() => {
    console.log('[Delete Patent] Executing actual delete for patent:', patent);
    console.log('[Delete Patent] Patent ID being deleted:', patent?.id);

    return webservice.wac
      .deletePatent({ id: patent.id })
      .then((result) => {
        console.log('[Delete Patent] Delete successful, result:', result);
        resetPatent();
        getPatents();
        return result;
      })
      .catch((error) => {
        console.error('[Delete Patent] Delete failed with error:', error);
        console.error('[Delete Patent] Error details:', {
          message: error.message,
          stack: error.stack,
          patentId: patent?.id,
        });
        displayError(error);
      });
  }, [patent, getPatents, resetPatent, displayError]);

  const savePatent = useCallback(
    (patentToSave) =>
      webservice.wac
        .savePatent(patentToSave)
        .then(() => {
          resetPatent();
          getPatents();
        })
        .catch((error) => {
          displayError(error);
          // Re-throw the error so the modal can also catch it and stop its own loading spinner.
          throw error;
        }),
    [getPatents, resetPatent, displayError],
  );

  return (
    <div key="listings" className="home-wrapper">
      <div className="home-content">
        <h1 className="mb-zero">Patent History</h1>
      </div>
      <div className="buttons flex">
        <Button
            label="Manually Enter History"
            className="mr-spacing flex align-items-center"
            onClick={() => editPatent(true, {}, true)}
            children={
                <span className="icon--round ml-spacing">{Icon.iconPencil()}</span>
            }
            childrenPostion={Button.CHILDREN_POSITION.AFTER}
        />
      </div>
      <div>
        <Table
          list={sortByLastUpdatedDesc(patents)}
          columns={columns(editPatent, deletePatent)}
          filters={true}
          showLimit={50}
          ajaxSpinner={isBusy}
        />
      </div>
      {showPatent && (
        <Patent
          onClose={resetPatent}
          onSave={savePatent}
          isNew={isNew}
          patent={patent}
          orgs={orgOptions}
        />
      )}
      {showDelete && (
        <PopupWindow
          className="popup-window--large"
          onCloseButtonCallback={resetPatent}
          footerChildren={[
            <Button
              label="Cancel"
              onClick={resetPatent}
              key="close-button"
              className="button--black"
            />,
            <Button
              label="Delete"
              busy={isBusy}
              onClick={actualDelete}
              key="delete-button"
            />,
          ]}
        >
          <h3>Are you sure?</h3>
          <p>You are about to delete a patent.</p>
        </PopupWindow>
      )}
    </div>
  );
};

export default observer(Patents);
